import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    type CreateExpensePayload,
    type Expense,
    type ExpenseSummary,
    type SelectOption,
    type UpdateExpensePayload,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

// Fallback local das categorias de despesa, caso o page-form não responda (mesma ideia dos
// demais selects do módulo — o dropdown não fica vazio). A fonte da verdade é o backend.
const CATEGORY_FALLBACK: SelectOption[] = [
    { value: 'COURT_COSTS', label: 'Custas judiciais' },
    { value: 'LITIGATION_FEES', label: 'Honorários advocatícios contencioso' },
    { value: 'ADVISORY_FEES', label: 'Honorários advocatícios consultivo' },
    { value: 'REGISTRATION', label: 'Despesas de registro' },
    { value: 'COLLATERAL_MONITORING', label: 'Monitoramento de garantias' },
    { value: 'CREDIT_ACQUISITION_RTD_RI', label: 'Aquisição de créditos para RTD e RI' },
    { value: 'CERTIFICATE_ISSUANCE', label: 'Emissão de certidões' },
    { value: 'NOTARIAL_DEED', label: 'Ata notarial' },
    { value: 'APPRAISAL_TRANSPORT', label: 'Avaliação/transportes de maquinários' },
    { value: 'INPI', label: 'INPI' },
    { value: 'OTHER', label: 'Outro' },
];

const BENEFICIARY_TYPE_FALLBACK: SelectOption[] = [
    { value: 'LAW_FIRM', label: 'Escritório de advocacia' },
    { value: 'COURT', label: 'Tribunal' },
    { value: 'NOTARY', label: 'Tabelião/Cartório' },
    { value: 'ONR', label: 'ONR' },
    { value: 'RTD', label: 'RTD' },
    { value: 'IEPTB', label: 'IEPTB' },
    { value: 'INPI', label: 'INPI' },
    { value: 'APPRAISER', label: 'Avaliador' },
    { value: 'CARRIER', label: 'Transportadora' },
    { value: 'OTHER', label: 'Outro' },
];

// Valor do formulário de despesa dentro do caso (a categoria e o valor são o mínimo; o caso
// já vem do contexto da seção, então não é campo do form).
interface AddCaseExpenseValue extends Omit<CreateExpensePayload, 'legalCaseId'> {}

const EMPTY_SUMMARY: ExpenseSummary = {
    total: 0,
    totalFormatted: null,
    count: 0,
    byCategory: [],
};

// Gerencia as despesas de um caso (seção "Custos" do detalhe): lista com o agregado calculado
// (total + quebra por categoria), adiciona, edita e remove. Despesa é raiz própria com rotas
// top-level (/expenses) — a lista é filtrada por legalCaseId, e o caso é enviado no payload.
// Mesmo padrão do useLegalCaseDeposits (seededCaseRef re-seed por caso, guarda de corrida por
// geração), mas sem rota aninhada.
export default function useLegalCaseExpenses(
    caseId: string | null,
    initialExpenses?: Expense[],
    initialSummary?: ExpenseSummary,
) {
    const [expenses, setExpenses] = useState<Expense[]>(initialExpenses || []);
    const [summary, setSummary] = useState<ExpenseSummary>(initialSummary || EMPTY_SUMMARY);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>(CATEGORY_FALLBACK);
    const [beneficiaryTypeOptions, setBeneficiaryTypeOptions] =
        useState<SelectOption[]>(BENEFICIARY_TYPE_FALLBACK);
    const seededCaseRef = useRef<string | null>(null);
    const fetchGenRef = useRef(0);

    const fetchExpenses = useCallback(async () => {
        if (!caseId) return;
        const generation = ++fetchGenRef.current;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getExpenses({ legalCaseId: caseId });
            if (fetchGenRef.current !== generation) return;
            setExpenses(result.items || []);
            setSummary(result.summary || EMPTY_SUMMARY);
        } catch {
            // silencioso — a seção já mostra o que veio no page-view
        } finally {
            if (fetchGenRef.current === generation) setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setExpenses(initialExpenses || []);
        setSummary(initialSummary || EMPTY_SUMMARY);
        setErrorFields([]);
        // Semeia com o page-view (paint imediato) e re-busca do servidor — cobre a remontagem
        // ao alternar de aba (senão o lançamento recém-criado sumiria até recarregar a página).
        fetchExpenses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    // Opções dos selects (categoria, tipo de beneficiário) do page-form; fallback local se falhar.
    useEffect(() => {
        let active = true;
        recoveryApi.getLegalCaseFormOptions().then((opts) => {
            if (!active || !opts) return;
            if (opts.expenseCategories?.length) setCategoryOptions(opts.expenseCategories);
            if (opts.beneficiaryTypes?.length) setBeneficiaryTypeOptions(opts.beneficiaryTypes);
        }).catch(() => { /* fallback local já preenche */ });
        return () => { active = false; };
    }, []);

    const clearErrors = () => setErrorFields([]);

    const addExpense = async (data: AddCaseExpenseValue): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.createExpense({ ...data, legalCaseId: caseId });
            await fetchExpenses();
            toast.success('Despesa registrada');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao registrar despesa');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const updateExpense = async (
        expenseId: string,
        data: UpdateExpensePayload,
    ): Promise<boolean> => {
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.updateExpense(expenseId, data);
            await fetchExpenses();
            toast.success('Despesa atualizada');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar despesa');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removeExpense = async (expenseId: string) => {
        setIsSaving(true);
        try {
            await recoveryApi.removeExpense(expenseId);
            await fetchExpenses();
            toast.success('Despesa removida');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover despesa');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        expenses, summary, isLoading, isSaving, errorFields,
        categoryOptions, beneficiaryTypeOptions,
        addExpense, updateExpense, removeExpense, clearErrors,
    };
}

export type { AddCaseExpenseValue };
